"use client";

/**
 * Hero blinds close-over (Venetian recipe 1).
 *
 * Wraps the existing hero content and overlays N vertical gold strips
 * (52 desktop, 35 tablet, 25 mobile), transform-origin left center,
 * initially rotateY(-90deg) so they are edge-on and invisible. Leaving
 * the hero scrubs rotationY to 0 (stagger 0.005, power3.out,
 * start "top+=10% top", end "+=125%"), closing gold venetian blinds
 * over the hero.
 *
 * The strips only exist client-side (rendered after mount), so no-JS
 * clients and crawlers get the untouched hero. Reduced-motion users
 * never get strips at all.
 */
import {
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
} from "react";
import { gsap, useGSAP, prefersReducedMotion } from "./gsap";

export interface BlindsHeroProps extends HTMLAttributes<HTMLDivElement> {
  /** Strip colour. Defaults to Silk gold. */
  stripColor?: string;
}

function stripCountFor(width: number): number {
  if (width >= 1024) return 52;
  if (width >= 768) return 35;
  return 25;
}

export function BlindsHero({
  children,
  stripColor = "#C9A96E",
  className = "",
  ...rest
}: BlindsHeroProps) {
  const ref = useRef<HTMLDivElement>(null);
  const stripsRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const compute = () => setCount(stripCountFor(window.innerWidth));
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useGSAP(
    () => {
      if (!count || prefersReducedMotion()) return;
      const strips = stripsRef.current?.children;
      if (!strips || !strips.length) return;
      gsap.set(strips, { rotationY: -90, transformOrigin: "left center" });
      gsap.to(strips, {
        rotationY: 0,
        stagger: 0.005,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top+=10% top",
          end: "+=125%",
          scrub: true,
        },
      });
    },
    // Strip count changes on resize: revert and rebuild the tween against
    // the fresh strip set.
    { scope: ref, dependencies: [count], revertOnUpdate: true }
  );

  return (
    <div ref={ref} className={`relative ${className}`} {...rest}>
      {children}
      {count !== null && (
        <div
          ref={stripsRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20 flex overflow-hidden"
        >
          {Array.from({ length: count }, (_, i) => (
            <div
              key={i}
              className="h-full flex-1"
              style={{
                backgroundColor: stripColor,
                // Edge-on before GSAP takes over: invisible by default.
                transform: "rotateY(-90deg)",
                transformOrigin: "left center",
                backfaceVisibility: "hidden",
                // Overlap a hair so closed blinds show no seams.
                marginRight: "-0.5px",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
